import { Fragment, useEffect, useState } from "react";
import { api, fmtEur, fmtDate, formatErr, canWrite, COMPANY_COLORS, CATEGORIE_LABELS, currentCompetenzaMese } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MonthPicker } from "@/components/MonthPicker";
import { PadroncinoSelect, AZIENDA } from "@/components/PadroncinoSelect";
import { CostiCarte } from "@/components/CostiCarte";
import { MezzoSelect } from "@/components/MezzoSelect";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Split, Send, Undo2, ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import SplitDialog from "@/components/SplitDialog";

export default function Consumi() {
  const { company } = useCompany();
  const { user } = useAuth();
  const writable = canWrite(user, "movimenti");
  const [mese, setMese] = useState(currentCompetenzaMese());
  const [tab, setTab] = useState("da_riportare");
  const [tipo, setTipo] = useState("dkv");
  const [refresh, setRefresh] = useState(0);
  const [all, setItems] = useState([]);
  const tipoDi = (m) => m.fonte === "telepass" ? (m.telepass_dkv || (m.descrizione || "").startsWith("TELEPASS DKV") ? "telepass_dkv" : "telepass") : m.fonte;
  const items = all.filter((m) => tipoDi(m) === tipo);
  const conta = (t) => all.filter((m) => tipoDi(m) === t).length;
  const TIPI = [["dkv", "DKV"], ["q8", "Q8"], ["uta", "UTA"], ["telepass_dkv", "Telepass DKV"], ["telepass", "Telepass"]];
  const tipoLbl = Object.fromEntries(TIPI);
  const [padroncini, setPadroncini] = useState([]);
  const [sel, setSel] = useState([]);
  const [open, setOpen] = useState({});
  const [splitMov, setSplitMov] = useState(null);

  const load = async () => {
    const req = (load.seq = (load.seq || 0) + 1);
    const { data } = await api.get("/movimenti", { params: { company, mese, area: "tessere", staging: tab === "da_riportare" } });
    if (req !== load.seq) return;
    setItems(data); setSel([]); setRefresh((n) => n + 1);
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [company, mese, tab]);
  const [veicoli, setVeicoli] = useState([]);
  const [tessMap, setTessMap] = useState({});
  useEffect(() => {
    api.get("/padroncini", { params: { company } }).then((r) => setPadroncini(r.data));
    api.get("/veicoli", { params: { company } }).then((r) => setVeicoli(r.data));
    api.get("/tessere", { params: { company } }).then((r) => setTessMap(Object.fromEntries(r.data.map((t) => [t.id, t.veicolo_id]))));
  }, [company]);
  const pMap = Object.fromEntries(padroncini.map((p) => [p.id, `${p.codice} · ${p.nome || ""} ${p.cognome || ""}`.trim()]));

  const cambia = (m, pid) => patchM(m, pid === AZIENDA ? { uso_aziendale: true } : { padroncino_id: pid });
  const patchM = async (m, body) => {
    try { await api.patch(`/movimenti/${m.id}`, body); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const riporta = async (ids, flag) => {
    try {
      const { data } = await api.post("/movimenti/riporta", { ids, riporta: flag });
      const n = data.aggiornati;
      toast.success(flag ? `${n} ${n === 1 ? "spesa riportata" : "spese riportate"} nei conteggi` : `${n} ${n === 1 ? "spesa tolta" : "spese tolte"} dai conteggi`);
      if (data.saltati.length) toast.warning(`Saltate (manca padroncino o mese chiuso): ${data.saltati.length}`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const elimina = async (ids) => {
    if (!window.confirm(ids.length === 1 ? "Eliminare questa transazione?" : `Eliminare ${ids.length} transazioni?`)) return;
    let ok = 0;
    for (const id of ids) {
      try { await api.delete(`/movimenti/${id}`); ok++; }
      catch (e) { toast.error(formatErr(e.response?.data?.detail)); break; }
    }
    if (ok) toast.success(`${ok} ${ok === 1 ? "transazione eliminata" : "transazioni eliminate"}`);
    load();
  };
  const assegnati = items.filter((m) => m.padroncino_id || m.uso_aziendale).map((m) => m.id);
  const tot = items.reduce((a, m) => a + m.importo, 0);
  const estero = items.filter((m) => m.estera).reduce((a, m) => a + m.importo, 0);
  const imponibile = items.filter((m) => !m.estera).reduce((a, m) => a + m.importo / 1.22, 0);
  const iva = tot - estero - imponibile;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-black">Consumi importati</h1>
          <div className="text-sm text-muted-foreground">Gasolio (DKV, UTA, Q8) e passaggi Telepass importati. Assegna o suddividi, poi riportali nei conteggi. Non compaiono in Prima nota.</div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs font-heading uppercase">Mese</Label>
          <MonthPicker value={mese} onChange={setMese} testId="consumi-mese" />
          {writable && <Button size="sm" variant="outline" className="h-8 gap-1 text-red-600" disabled={!sel.length} onClick={() => elimina(sel)} data-testid="consumi-elimina-sel"><Trash2 className="w-4 h-4" />Elimina selezionati ({sel.length})</Button>}
          {writable && tab === "riportati" &&
            <Button size="sm" variant="outline" className="h-8 gap-1" disabled={!sel.length} onClick={() => riporta(sel, false)} data-testid="consumi-annulla-sel"><Undo2 className="w-4 h-4" />Togli selezionati ({sel.length})</Button>}
          {writable && tab === "da_riportare" && <>
            <Button size="sm" variant="outline" className="h-8 gap-1" disabled={!sel.length} onClick={() => riporta(sel, true)} data-testid="consumi-riporta-sel"><Send className="w-4 h-4" />Riporta selezionati ({sel.length})</Button>
            <Button size="sm" className="h-8 gap-1" disabled={!assegnati.length} onClick={() => riporta(assegnati, true)} data-testid="consumi-riporta-tutti"><Send className="w-4 h-4" />Riporta tutti gli assegnati ({assegnati.length})</Button>
          </>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={tipo} onValueChange={(v) => { setTipo(v); setSel([]); }}>
          <TabsList>
            {TIPI.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`consumi-tipo-${v}`}>{l} ({conta(v)})</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="da_riportare" data-testid="consumi-tab-da-riportare">Da riportare</TabsTrigger>
            <TabsTrigger value="riportati" data-testid="consumi-tab-riportati">Già nei conteggi</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <Card className="rounded-md overflow-hidden">
        <table className="autotrans-table w-full" data-testid="consumi-table">
          <thead><tr>
            <th className="w-8"><Checkbox checked={items.length > 0 && sel.length === items.length} onCheckedChange={(v) => setSel(v ? items.map((m) => m.id) : [])} data-testid="consumi-sel-all" /></th>
            <th className="w-8"></th><th>Data</th><th>Fonte</th><th>Descrizione</th><th>Padroncino</th><th>Mezzo</th><th>Azienda</th><th className="text-right">Importo</th><th className="w-28">Azioni</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={10} className="text-center py-6 text-muted-foreground">Nessun consumo {tipoLbl[tipo]} {tab === "da_riportare" ? "da riportare" : "riportato"} per questo mese</td></tr>}
            {items.map((m) => (
              <Fragment key={m.id}>
                <tr data-testid={`consumi-row-${m.id}`} className={!m.padroncino_id && !m.uso_aziendale ? "bg-orange-50 dark:bg-orange-900/10" : ""}>
                  <td><Checkbox checked={sel.includes(m.id)} onCheckedChange={() => setSel((s) => s.includes(m.id) ? s.filter((x) => x !== m.id) : [...s, m.id])} data-testid={`consumi-sel-${m.id}`} /></td>
                  <td>{m.dettaglio?.length > 0 && <button onClick={() => setOpen({ ...open, [m.id]: !open[m.id] })} data-testid={`consumi-expand-${m.id}`}>
                    {open[m.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>}</td>
                  <td>{fmtDate(m.data)}</td>
                  <td><Badge variant="outline" className="h-5 uppercase">{m.fonte}</Badge>
                    {m.estera && <Badge className="h-5 ml-1 bg-sky-600 hover:bg-sky-600" title="Fattura estera già ivata: importo = totale fattura, esclusa dall'IVA DKV" data-testid={`consumi-estera-${m.id}`}>Estera {m.paese}</Badge>}</td>
                  <td>{m.descrizione}{m.note && <div className="text-xs text-muted-foreground">{m.note}</div>}
                    {m.dettaglio?.length > 0 && <div className="text-xs text-muted-foreground">{m.dettaglio.length} operazioni · {CATEGORIE_LABELS[m.categoria]}</div>}</td>
                  <td className="min-w-[200px]">{writable && tab === "da_riportare" ? (
                    <PadroncinoSelect value={m.uso_aziendale ? AZIENDA : m.padroncino_id || ""} onChange={(v) => cambia(m, v)} aziendaOption
                      padroncini={padroncini.filter((p) => p.company === m.company)} testId={`consumi-padroncino-${m.id}`} />
                    ) : (m.uso_aziendale ? "AZIENDA (uso interno)" : pMap[m.padroncino_id] || "—")}</td>
                  <td className="min-w-[130px]"><MezzoSelect m={m} veicoli={veicoli} tessMap={tessMap} disabled={!writable || tab !== "da_riportare"}
                        onChange={(v) => patchM(m, { veicolo_id: v })} testId={`consumi-mezzo-${m.id}`} /></td>
                  <td><Badge style={{ backgroundColor: COMPANY_COLORS[m.company], color: "white" }} className="h-5">{m.company}</Badge></td>
                  <td className="text-right font-mono">{fmtEur(m.importo)}</td>
                  <td className="whitespace-nowrap">{writable && (tab === "da_riportare" ? <>
                    <Button size="icon" variant="ghost" className="h-7 w-7" title="Suddividi tra più padroncini" onClick={() => setSplitMov(m)} data-testid={`consumi-split-${m.id}`}><Split className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" title="Riporta nel conteggio" disabled={!m.padroncino_id && !m.uso_aziendale} onClick={() => riporta([m.id], true)} data-testid={`consumi-riporta-${m.id}`}><Send className="w-3.5 h-3.5" /></Button>
                  </> : <Button size="icon" variant="ghost" className="h-7 w-7" title="Togli dal conteggio" onClick={() => riporta([m.id], false)} data-testid={`consumi-annulla-${m.id}`}><Undo2 className="w-3.5 h-3.5" /></Button>)}
                    {writable && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" title="Elimina transazione" onClick={() => elimina([m.id])} data-testid={`consumi-elimina-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}</td>
                </tr>
                {open[m.id] && (
                  <tr><td colSpan={10} className="bg-muted/30">
                    <table className="w-full text-xs">
                      <tbody>{m.dettaglio.map((d, i) => (
                        <tr key={i}><td className="w-24 py-0.5">{fmtDate(d.data)}</td><td>{d.luogo}</td><td>{d.prodotto || ""}</td>
                          <td className="w-24">{d.quantita ? `${d.quantita} ${d.unita || ""}` : ""}</td><td className="w-28 text-right font-mono">{fmtEur(d.importo)}</td></tr>
                      ))}</tbody>
                    </table>
                  </td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
          {items.length > 0 && <tfoot>
            <tr><td colSpan={8} className="text-right text-xs text-muted-foreground">Imponibile</td><td className="text-right font-mono" data-testid="consumi-imponibile">{fmtEur(imponibile)}</td><td /></tr>
            <tr><td colSpan={8} className="text-right text-xs text-muted-foreground">IVA 22%</td><td className="text-right font-mono" data-testid="consumi-iva">{fmtEur(iva)}</td><td /></tr>
            {estero > 0 && <tr><td colSpan={8} className="text-right text-xs text-muted-foreground">Fatture estere (già ivate, IVA non recuperabile)</td><td className="text-right font-mono" data-testid="consumi-estero">{fmtEur(estero)}</td><td /></tr>}
            <tr><td colSpan={8} className="text-right font-heading font-bold">Totale {tipoLbl[tipo]}</td><td className="text-right font-mono font-bold" data-testid="consumi-totale">{fmtEur(tot)}</td><td /></tr>
          </tfoot>}
        </table>
      </Card>
      <CostiCarte company={company} mese={mese} writable={writable} refresh={refresh} />
      <SplitDialog movimento={splitMov} onClose={() => setSplitMov(null)} onDone={() => { setSplitMov(null); load(); }} />
    </div>
  );
}
